import React from 'react';

import icon from '@assets/img/icons/security-settings.svg';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { LogForm } from '@components/Form';
import { TextInput, TextInputProps } from '@components/Forms/TextInput';
import { Layout } from '@components/Layout';
import { AccountManagerContainer } from '@components/settings/AccountManagerContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { usePatchPassword } from '@hooks/mutation/usePatchPassword';
import { useUser } from '@hooks/queries/useUser';
import { AccountManagerForm } from '@models/AccountManagerForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, Field } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import { Form, Formik } from 'formik';

const strings = createStrings({
  ja: {
    account: 'アカウント設定',
    title: 'セキュリティー',
    update: '変更',
    success: 'パスワードを更新しました',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  formContainer: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    padding: '30px 1rem',
  },
  formInner: {
    ...common.flexColumn,
    ...common.verticalSpace1,
    maxWidth: '30rem',
    margin: '0 auto',
  },
  prompt: {
    ...common.normalText,
    paddingBottom: '2rem',
  },
  inlineForm: {
    flex: 1,
  },
  firstNameLastNameContainer: {
    ...common.flexInline,
    alignItems: 'flex-end',
  },
  firstNameLastNameDivider: {
    width: 10,
    height: 1,
    backgroundColor: theme.palette.grey['500'],
    margin: '0 10px 22px 10px',
  },
  privatePolicy: {
    ...common.middleText,
    color: theme.palette.grey['500'],
  },
  privatePolicyLink: {
    color: theme.palette.primary.main,
  },
}));

const schema = AccountManagerForm.shared.toSecurityUpdateSchema();

const validate = createValidate(schema);

const initialValue = {
  password: '',
  newPassword: '',
  confirmNewPassword: '',
};

export const Security: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { mutateAsync, isError, isLoading, error, isSuccess, reset } = usePatchPassword();
  const user = useUser();

  const renderTextField = (field: Field<any>, props?: Partial<TextInputProps>) => {
    return (
      <TextInput
        label={field.label}
        name={field.name}
        itemVariant="column"
        itemErrorVariant="inline"
        {...props}
      />
    );
  };

  return (
    <Layout
      loading={user.isLoading}
      title={strings.title}
      breadcrumbs={[
        '/utilities/Dashboard',
        { text: strings.account },
        '/settings/AccountManager/Security',
      ]}
    >
      {() => (
        <AccountManagerContainer>
          <Dialog open={isError} onClose={reset}>
            {error instanceof FormError ? error.getDisplayMessageLegacy() : (error as any)?.message}
          </Dialog>

          <Dialog
            open={isSuccess}
            onClose={() => {
              window.location.reload();
            }}
          >
            {strings.success}
          </Dialog>

          <Formik
            initialValues={initialValue}
            validate={validate}
            onSubmit={async (values) => {
              await mutateAsync({
                new_password1: values.newPassword,
                new_password2: values.confirmNewPassword,
                old_password: values.password,
              });
            }}
          >
            <Form data-testid="AccountSecurity" className={classes.formContainer}>
              <SpinnerModal open={isLoading} />
              <LogForm name="security-settings" disabled />
              <div className={classes.formInner}>
                {renderTextField(AccountManagerForm.shared.password, {
                  type: 'password',
                  autoComplete: 'new-password',
                })}

                {renderTextField(AccountManagerForm.shared.newPassword, {
                  type: 'password',
                  autoComplete: 'new-password',
                })}

                {renderTextField(AccountManagerForm.shared.confirmNewPassword, {
                  type: 'password',
                  autoComplete: 'new-password',
                })}

                <div />
                <Button text={strings.update} color="primary" />
              </div>
            </Form>
          </Formik>
        </AccountManagerContainer>
      )}
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  icon,
};
